import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Typography } from '@mui/material'

import { useGetScreenshotsByGameIdsQuery } from '../../../api/core'
import utilsService from '../../../services/UtilsService'
import { useFeatures } from '../../feature/hooks/useFeatures'
import { Game } from '../../game'
import { FeatureGame } from '../../game/types/Game'
import FilterByTags from '../../implementation-examples/components/FilterByTags/FilterByTags'
import ImplementationPreviews from '../../implementation-examples/components/ImplementationPreviews/ImplementationPreviews'
import { enabledFeaturesFilter } from '../../implementation-examples/util/filters'
import { convertLegacyScreenshots, extractNeededLegacyIds, pickFeaturesWithLegacyId } from '../../implementation-examples/util/screenshotHelpers'

type ImplementationExamplesSubtabProps = {
  game: Game
}

const ImplementationExamplesSubtab: React.FC<ImplementationExamplesSubtabProps> = ({ game }) => {
  const { t } = useTranslation()
  const { data: featuresData, isFetching: isFetchingFeatures } = useFeatures({ skipCounts: true, includeHiddenScreenshots: true })
  const { data: screenshotsData, isFetching: isFetchingScreenshots } = useGetScreenshotsByGameIdsQuery([game.id])
  const [enabledFeatureIds, setEnabledFeatureIds] = useState<number[]>()

  const featureGame: FeatureGame = useMemo(
    () =>
      ({
        ...game,
        featureScreenshots: convertLegacyScreenshots((screenshotsData || []).filter(({ gameId }) => gameId === game.id)),
      } as FeatureGame),
    [game, screenshotsData]
  )

  const features = useMemo(() => {
    if (!featuresData) {
      return undefined
    }
    const legacyIds = extractNeededLegacyIds([featureGame])
    return pickFeaturesWithLegacyId(legacyIds, featuresData)
  }, [featuresData, featureGame])

  const sortedReleaseDate = useMemo(() => {
    return utilsService.sortedReleaseDate(featureGame)
  }, [featureGame])

  if (!screenshotsData || !screenshotsData.length) {
    return <Typography>{t('common:no_implementations_found')}</Typography>
  }

  return (
    <>
      <Typography>{t('implementations:label_filter_implementations_2')}</Typography>
      <FilterByTags features={features} onChangeEnabledFeatures={setEnabledFeatureIds} />
      <ImplementationPreviews
        breakDownVersions={true}
        features={features?.filter(enabledFeaturesFilter(enabledFeatureIds))}
        games={[featureGame]}
        loading={isFetchingFeatures || isFetchingScreenshots}
        hideListScreenshots
        fromDate={sortedReleaseDate}
        minDate={sortedReleaseDate}
      />
    </>
  )
}

export default ImplementationExamplesSubtab
